﻿import { ensureJQuery, ensureParsley } from "@js/core/loaders/vendors";
import { showToast } from "@js/components/common/toast";
import { t, currentLang } from "@js/core/utils/i18n";
function setButtonSuccess(btn) {
    if (!btn) return;

    if (!btn._originalSvg) {
        btn._originalSvg = btn.innerHTML;
    }

    btn.innerHTML = `
        <svg viewBox="0 0 64 64" fill="none" aria-hidden="true">
            <circle cx="32" cy="32" r="28"
                    stroke="currentColor"
                    stroke-width="4"
                    opacity="0.2"></circle>

            <path d="M18 33.5L27.5 43L46.5 22.5"
                  stroke="currentColor"
                  stroke-width="5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  style="
                    stroke-dasharray: 42;
                    stroke-dashoffset: 42;
                    animation: ibanCheckAnim 0.42s ease forwards;
                  " />
        </svg>
    `;

    btn.classList.add("is-success");

    clearTimeout(btn._successTimer);
    btn._successTimer = window.setTimeout(() => {
        btn.innerHTML = btn._originalSvg;
        btn.classList.remove("is-success");
        btn._originalSvg = null;
    }, 3000);
}

function addCopyRipple(btn, event) {
    if (!btn) return;

    const oldRipple = btn.querySelector(".copy-ripple");
    if (oldRipple) oldRipple.remove();

    const rect = btn.getBoundingClientRect();
    const ripple = document.createElement("span");

    ripple.className = "copy-ripple";
    ripple.style.left = `${event.clientX - rect.left}px`;
    ripple.style.top = `${event.clientY - rect.top}px`;

    btn.appendChild(ripple);
    window.setTimeout(() => ripple.remove(), 650);
}

async function copyText(text, successMessage, emptyMessage) {
    if (!text || !text.trim()) {
        showToast(emptyMessage || t("Copy.Empty"), { type: "error" });
        return false;
    }

    try {
        await navigator.clipboard.writeText(text);
        showToast(successMessage || t("Copy.Done"));
        return true;
    } catch {
        const tempInput = document.createElement("textarea");
        tempInput.value = text;
        tempInput.style.position = "fixed";
        tempInput.style.left = "-9999px";
        tempInput.style.top = "0";

        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand("copy");
        document.body.removeChild(tempInput);

        showToast(successMessage || t("Copy.Done"));
        return true;
    }
}

function initCopyEmailButtons() {
    document.querySelectorAll(".copy-email-btn").forEach((btn) => {
        btn.addEventListener("click", async function (event) {
            addCopyRipple(this, event);

            const email = this.dataset.email || "";

            const copied = await copyText(
                email,
                t("Contact.EmailCopied"),
                t("Contact.EmailEmpty")
            );

            if (copied) {
                setButtonSuccess(this);
            }
        });
    });
}

function registerContactValidators() {
    const Parsley = window.Parsley;

    if (!Parsley) return;

    // 🔹 Fullname → en az 2 kelime, her biri min 2 harf
    Parsley.addValidator("fullname", {
        validateString: function (value) {
            if (!value) return false;

            const words = value
                .trim()
                .split(/\s+/)
                .filter(w => w.length > 0);

            if (words.length < 2) return false;

            return words.every(w => w.length >= 2);
        },
        messages: {
            [currentLang()]: t("Validation.FullName")
        }
    });

    // 🔹 Subject → min 2 karakter
    Parsley.addValidator("minsubject", {
        validateString: function (value) {
            return (value || "").trim().length >= 2;
        },
        messages: {
            [currentLang()]: t("Validation.Subject")
        }
    });

    // 🔹 Message → en az 2 kelime
    Parsley.addValidator("minwords", {
        validateString: function (value) {
            if (!value) return false;

            const words = value
                .trim()
                .split(/\s+/)
                .filter(w => w.length > 0);

            return words.length >= 2;
        },
        messages: {
            [currentLang()]: t("Validation.Message")
        }
    });
}

function playSubmitAnimation(btn) {
    return new Promise(resolve => {
        const icon = btn.querySelector("i");
        if (!icon) return resolve();

        const rect = icon.getBoundingClientRect();

        // uçan ikon
        const flying = document.createElement("div");
        flying.className = "flying-icon";
        document.body.appendChild(flying);

        flying.innerHTML = `
            <svg viewBox="0 0 24 24" fill="none">
                <path d="M2 12L22 2L13 22L11 13L2 12Z"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linejoin="round"/>
            </svg>
        `;

        btn.classList.add("is-flying");

        // başlangıç
        let startX = rect.left;
        let startY = rect.top;

        // hedef (sağ üst köşe gibi)
        let endX = window.innerWidth - 100;
        let endY = 80;

        // bezier control noktaları
        const cp1x = startX + 120;
        const cp1y = startY - 60;

        const cp2x = endX - 120;
        const cp2y = endY + 40;

        let startTime = null;
        const duration = 1400;

        function cubicBezier(t, p0, p1, p2, p3) {
            return (
                Math.pow(1 - t, 3) * p0 +
                3 * Math.pow(1 - t, 2) * t * p1 +
                3 * (1 - t) * Math.pow(t, 2) * p2 +
                Math.pow(t, 3) * p3
            );
        }

        function createParticle(x, y) {
            const p = document.createElement("div");
            p.className = "particle";
            p.style.left = x + "px";
            p.style.top = y + "px";
            document.body.appendChild(p);

            setTimeout(() => p.remove(), 800);
        }

        function animate(timestamp) {
            if (!startTime) startTime = timestamp;
            const elapsed = timestamp - startTime;
            let t = Math.min(elapsed / duration, 1);

            // easing (çok önemli)
            t = 1 - Math.pow(1 - t, 3);

            const x = cubicBezier(t, startX, cp1x, cp2x, endX);
            const y = cubicBezier(t, startY, cp1y, cp2y, endY);

            flying.style.left = x + "px";
            flying.style.top = y + "px";

            // rotation
            flying.style.transform = `rotate(${t * 360}deg) scale(${1 - t * 0.4})`;

            // particle trail
            if (Math.random() > 0.5) {
                createParticle(x, y);
            }

            if (t < 1) {
                requestAnimationFrame(animate);
            } else {
                flying.remove();
                resolve();
            }
        }

        requestAnimationFrame(animate);
    });
}

function playSubmitAnimationV1(btn) {
    const icon = btn.querySelector("i");
    if (!icon) return Promise.resolve();

    const rect = icon.getBoundingClientRect();

    const flying = document.createElement("div");
    flying.className = "flying-icon";
    flying.style.left = rect.left + "px";
    flying.style.top = rect.top + "px";

    flying.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none">
            <path d="M2 12L22 2L13 22L11 13L2 12Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linejoin="round"/>
        </svg>
    `;

    document.body.appendChild(flying);

    // trail oluştur
    const trail = document.createElement("div");
    trail.className = "flying-trail";
    trail.style.left = rect.left - 50 + "px";
    trail.style.top = rect.top - 50 + "px";
    document.body.appendChild(trail);

    btn.classList.add("is-flying");

    return new Promise(resolve => {
        setTimeout(() => {
            flying.remove();
            trail.remove();
            resolve();
        }, 1400);
    });
}

async function initContactParsley() {
    const form = document.getElementById("contactForm");
    if (!form) return;

    await ensureJQuery();
    await ensureParsley(currentLang());

    registerContactValidators();

    const $ = window.jQuery;
    const $form = $(form);

    $form.parsley({
        errorClass: "is-invalid",
        successClass: "is-valid",
        classHandler(field) {
            return field.$element.closest(".field");
        },
        errorsContainer(field) {
            return field.$element.closest(".field");
        }
    });

    $form.off("submit.contact").on("submit.contact", async function (e) {
        const parsley = $form.parsley();
        const submitBtn = form.querySelector(".submit-btn");

        if (!parsley.isValid()) {
            e.preventDefault();
            parsley.validate();
            return false;
        }

        e.preventDefault();

        await playSubmitAnimation(submitBtn);

        submitBtn?.classList.add("is-loading");
        submitBtn?.setAttribute("disabled", "disabled");

        form.submit();
        //return true;
    });
}

export async function initContactPage() {
    const page = document.querySelector(".contact-modern-page");
    if (!page) return;

    initCopyEmailButtons();
    await initContactParsley();
}